import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import HeroCarousel from "@/components/HeroCarousel";
import { AlertsSignup } from "@/components/AlertsSignup";
import { BetaFeedbackButton } from "@/components/BetaFeedback";
import { LaunchSignup } from "@/components/LaunchSignup";
import { PollWidget } from "@/components/PollWidget";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import { usePlatformCopy } from "@/hooks/usePlatformCopy";
import { api } from "@/lib/api";
import { COPY } from "@/lib/copy";
import {
  ShieldCheck, MapPin, Users, Fingerprint, Search, CheckCircle2, FileCheck2, Scale, ChevronRight,
  Newspaper, ExternalLink, CalendarDays, Landmark, Map as MapIcon, UserPlus, Vote, Link2, Sparkles,
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.24 },
};

const ECOSYSTEM_STEPS = [
  { icon: MapPin, title: "Find your location", text: "Enter your address to find your municipality and ward on the map.", to: "/elections/find-my-ward" },
  { icon: Landmark, title: "Know your election", text: "See offices up for election, key dates and official sources.", to: "/elections" },
  { icon: Users, title: "See who is running", text: "Browse candidates — including those who support election transparency.", to: "/candidates" },
  { icon: ShieldCheck, title: "Witness your vote", text: "After voting officially, create your voluntary verifiable declaration.", to: "/declare" },
  { icon: Fingerprint, title: "Verify your receipt", text: "Confirm your record is preserved, unaltered, any time.", to: "/verify" },
  { icon: Newspaper, title: "Stay informed", text: "Follow election news and get alerts for your community.", to: "/alerts" },
];

const STEPS = [
  { icon: Search, title: "Find your candidate", text: "Look up a participating candidate in your municipality and ward." },
  { icon: CheckCircle2, title: "Vote privately", text: "Cast your official vote exactly as usual. Your ballot stays secret." },
  { icon: FileCheck2, title: "Witness your vote", text: "Voluntarily declare which candidate you voted for — about two minutes." },
  { icon: Fingerprint, title: "Keep your receipt", text: "Get a unique number and SHA-256 hash proving your record is preserved." },
  { icon: Scale, title: "Verify the count", text: "After voting closes, the witnessed minimum can be compared with official results." },
];

export default function Home() {
  const copy = usePlatformCopy();
  const beta = useBetaStatus();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [munis, setMunis] = useState([]);
  const [elections, setElections] = useState([]);
  const [address, setAddress] = useState("");
  const [qotw, setQotw] = useState(null);

  useEffect(() => {
    api.get("/public/news", { params: { limit: 3 } }).then((r) => setNews(r.data)).catch(() => {});
    api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
    api.get("/public/elections").then((r) => setElections(r.data)).catch(() => {});
    api.get("/public/polls/question-of-week").then((r) => setQotw(r.data.poll)).catch(() => {});
  }, []);

  const submitAddress = (e) => {
    e.preventDefault();
    if (address.trim()) navigate(`/elections/find-my-ward?q=${encodeURIComponent(address.trim())}`);
  };

  return (
    <div>
      {/* FULL-WIDTH ROTATING HERO */}
      <HeroCarousel />

      {/* PHASE 12 — NOW OPEN IN BETA (soft launch hero, §23 preferred copy) */}
      {beta?.beta_mode && (
        <section className="bg-primary text-white border-b border-primary/50" aria-labelledby="beta-hero-title">
          <div className="wyv-container py-10 sm:py-14">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold tracking-widest px-3 py-1">
                  <Sparkles className="h-3 w-3" /> NOW OPEN IN BETA
                </span>
                <h2 id="beta-hero-title"
                    className="font-heading font-bold text-2xl sm:text-4xl leading-tight mt-4"
                    data-testid="beta-hero-title">
                  <span className="hidden sm:inline">{beta.messaging?.hero_title || "WITNESSYOURVOTE IS NOW OPEN IN BETA"}</span>
                  <span className="sm:hidden">{beta.messaging?.mobile_title || "WE'RE IN BETA"}</span>
                </h2>
                <p className="text-blue-100 mt-4 leading-relaxed max-w-xl hidden sm:block" data-testid="beta-hero-body">
                  {beta.messaging?.hero_body}
                </p>
                <p className="text-blue-100 mt-3 leading-relaxed sm:hidden">
                  {beta.messaging?.mobile_body || "Explore WitnessYourVote today. Sign up and be among the first to know when the full platform goes live."}
                </p>
                <p className="font-heading font-bold text-emerald-300 mt-4" data-testid="beta-hero-coming">
                  {beta.messaging?.hero_coming || "THE FULL PROGRAM IS COMING."}
                </p>
                <p className="text-sm text-blue-100 mt-1">{beta.messaging?.hero_signup_line}</p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="beta-hero-join">
                    <a href="#early-access">JOIN WITNESSYOURVOTE</a>
                  </Button>
                  <Button asChild size="lg" variant="outline"
                          className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                          data-testid="beta-hero-explore">
                    <Link to="/beta">EXPLORE THE BETA</Link>
                  </Button>
                </div>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hidden sm:block">
                <p className="font-heading font-bold text-sm tracking-wide" data-testid="beta-hero-first">
                  {beta.messaging?.hero_first || "BE THE FIRST TO GET ON WITNESSYOURVOTE"}
                </p>
                <div className="mt-3">
                  <LaunchSignup variant="inline" sourcePage="/ (hero)" />
                </div>
                <p className="text-xs text-blue-200 mt-4 italic" data-testid="beta-hero-support">
                  {beta.messaging?.hero_support || "Independent records. Citizen witnesses. Another layer of transparency."}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 1. WYV — ONE INTEGRATED EXPERIENCE (candidate transparency merged into the main story) */}
      <section className="wyv-container py-12 sm:py-16" aria-labelledby="ecosystem-title">
        <div className="max-w-3xl">
          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-4">Witness Your Vote</Badge>
          <h2 id="ecosystem-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
            One integrated experience — from your address to your independent record.
          </h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Witness Your Vote helps you find your municipality and ward, see who is running,
            discover <span className="font-semibold text-foreground">candidates who support election transparency</span>,
            and — after voting through the official process — create a voluntary, verifiable record of your vote.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {ECOSYSTEM_STEPS.map((s, i) => (
            <motion.div key={s.title} {...fadeUp} transition={{ duration: 0.24, delay: i * 0.04 }}>
              <Link to={s.to} className="block h-full group" data-testid={`ecosystem-${i}`}>
                <Card className="h-full hover:shadow-md hover:border-primary/40 transition-shadow duration-150">
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center shrink-0">
                      <s.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-foreground group-hover:text-primary">{s.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed mt-1">{s.text}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-7">
          <Button asChild className="bg-accent hover:bg-accent/90" data-testid="home-witness-cta">
            <Link to="/declare"><ShieldCheck className="h-4 w-4 mr-1.5" /> Witness My Vote</Link>
          </Button>
          <Button asChild variant="outline" data-testid="home-find-candidate-cta">
            <Link to="/candidates"><Users className="h-4 w-4 mr-1.5" /> Find a Candidate</Link>
          </Button>
          <Button asChild variant="outline" data-testid="home-how-cta">
            <Link to="/how-it-works">How It Works</Link>
          </Button>
        </div>
      </section>

      {/* 1.5 MUTUAL WITNESS — DON'T JUST DECLARE IT. WITNESS IT. */}
      <section className="bg-primary text-white" aria-labelledby="witness-it-title">
        <div className="wyv-container py-14 sm:py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Badge variant="outline" className="bg-white/10 text-blue-100 border-white/20 mb-4">Mutual Witness</Badge>
              <h2 id="witness-it-title" className="font-heading font-bold text-3xl sm:text-4xl leading-tight" data-testid="home-witness-it-title">
                DON'T JUST DECLARE IT.
                <br />
                <span className="text-emerald-300">WITNESS IT.</span>
              </h2>
              <p className="text-blue-100 mt-4 leading-relaxed max-w-lg">
                Make your declaration. Verify your identity. Have someone witness it. Witness theirs too.
                Keep a verifiable receipt. Your witness confirms that{" "}
                <span className="font-semibold text-white">you</span> made and signed your declaration —{" "}
                <span className="font-semibold text-white">they never see your ballot</span>.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="home-create-declaration-cta">
                  <Link to="/declare">MAKE MY DECLARATION</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="home-need-witness-cta">
                  <Link to="/witness-network">I NEED A WITNESS</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="home-how-mutual-works-cta">
                  <Link to="/how-it-works#mutual-witness">HOW IT WORKS</Link>
                </Button>
              </div>
            </div>
            <div className="space-y-3" data-testid="home-witness-steps">
              {[
                { n: "1", t: "Declare", d: "Say who you voted for — voluntarily, after voting officially." },
                { n: "2", t: "Verify", d: "Confirm your email address with one click." },
                { n: "3", t: "Sign", d: "Sign it electronically. You get a receipt with a unique ID." },
                { n: "4", t: "Get Witnessed", d: "Choose someone you trust — or ask us to find a participating witness." },
                { n: "5", t: "Verify Record", d: "Anyone with your receipt can check the record is intact." },
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-4 rounded-xl bg-white/5 border border-white/10 p-4">
                  <span className="h-9 w-9 shrink-0 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center">{s.n}</span>
                  <div>
                    <p className="font-heading font-semibold text-white">{s.t}</p>
                    <p className="text-sm text-blue-100">{s.d}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-4 rounded-xl bg-emerald-500/15 border border-emerald-300/30 p-4">
                <span className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-emerald-200 flex items-center justify-center">
                  <Link2 className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-emerald-200">Witness each other</p>
                  <p className="text-sm text-blue-100">If you both have declarations, witness each other — both become Mutually Witnessed.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1.7 COMMUNITY POLLS — YOUR COMMUNITY. YOUR VOICE. */}
      <section className="bg-secondary border-y border-slate-200" aria-labelledby="community-voice-title">
        <div className="wyv-container py-12">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl">
              <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Community Polls</Badge>
              <h2 id="community-voice-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground" data-testid="home-community-voice-title">
                YOUR COMMUNITY. YOUR VOICE.
              </h2>
              <p className="text-muted-foreground mt-2 leading-relaxed">
                What matters most where you live? Answer today's question and see what your community is saying.
                Voluntary polls — never scientific polling, never connected to your declaration.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90" data-testid="home-answer-poll-cta">
                <Link to="/polls">ANSWER TODAY'S POLL</Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="home-community-results-cta">
                <Link to="/community-voice">SEE COMMUNITY RESULTS</Link>
              </Button>
            </div>
          </div>
          {qotw && (
            <div className="mt-8 max-w-2xl" data-testid="home-question-of-week">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Question of the Week</p>
              <PollWidget poll={qotw} />
            </div>
          )}
        </div>
      </section>

      {/* 1.8 THREE SYSTEMS — CLEARLY SEPARATED */}
      <section className="bg-white border-b border-slate-200" aria-labelledby="three-systems-title">
        <div className="wyv-container py-12">
          <h2 id="three-systems-title" className="sr-only">Three separate systems</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="home-three-systems">
            <Card className="border-t-4 border-t-primary">
              <CardContent className="p-5">
                <p className="font-heading font-bold text-foreground inline-flex items-center gap-2"><Vote className="h-4 w-4 text-primary" /> WITNESS YOUR VOTE</p>
                <p className="text-sm text-muted-foreground mt-1">Declare it. Witness it. Verify it.</p>
                <Link to="/how-it-works" className="text-xs text-primary hover:underline mt-2 inline-block" data-testid="three-systems-wyv-link">How it works →</Link>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-accent">
              <CardContent className="p-5">
                <p className="font-heading font-bold text-foreground inline-flex items-center gap-2"><Users className="h-4 w-4 text-accent" /> COMMUNITY VOICE</p>
                <p className="text-sm text-muted-foreground mt-1">Tell us what matters. See what your community is saying.</p>
                <Link to="/community-voice" className="text-xs text-primary hover:underline mt-2 inline-block" data-testid="three-systems-voice-link">Community Voice →</Link>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-amber-500">
              <CardContent className="p-5">
                <p className="font-heading font-bold text-foreground inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-600" /> PITA — KAWARTHA LAKES</p>
                <p className="text-sm text-muted-foreground mt-1">Report it. Map it. Track it. Get answers.</p>
                <a href="https://stevelesperance.com/map" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1" data-testid="three-systems-pita-link">
                  External resource on SteveLesperance.com <ExternalLink className="h-3 w-3" />
                </a>
              </CardContent>
            </Card>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="three-systems-note">
            These three systems can link to each other, but their records are kept separate. Poll answers and
            issue reports are never combined with your voting declaration.
          </p>
        </div>
      </section>

      {/* INDEPENDENT CITIZEN WITNESSING */}
      <section className="bg-secondary/60 border-b border-slate-200" aria-labelledby="independence-title">
        <div className="wyv-container py-14">
          <div className="max-w-3xl">
            <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">
              Why independence matters
            </Badge>
            <h2 id="independence-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground"
                data-testid="independence-title">
              Independent Citizen Witnessing
            </h2>
            <p className="font-heading font-semibold text-accent mt-2" data-testid="independence-tagline">
              Independent records. Citizen witnesses. Another layer of transparency.
            </p>
            <p className="text-muted-foreground mt-4 leading-relaxed" data-testid="independence-body">
              WitnessYourVote creates an independent, citizen-witnessed record outside the official
              municipal election administration. This provides participating voters with a separate
              verification record while remaining independent from the municipality and its election
              officials.
            </p>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed" data-testid="independence-structural">
              Municipal elections are administered by municipalities whose councils are themselves being
              elected. WitnessYourVote provides participating citizens with a separate, independently
              witnessed record outside that administrative structure.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Button asChild className="bg-primary hover:bg-primary/90" data-testid="independence-cta">
                <Link to="/how-it-works"><Scale className="h-4 w-4 mr-1.5" /> How independent witnessing works</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/about">About Witness Your Vote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 2. FIND YOUR WARD / ELECTION CENTRE */}
      <section className="bg-white border-y border-slate-200" aria-labelledby="election-centre-title">
        <div className="wyv-container py-14">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-4">Ontario Election Centre</Badge>
              <h2 id="election-centre-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                Find your ward. Know your election.
              </h2>
              <p className="text-muted-foreground mt-3 leading-relaxed">
                Enter your address to locate your municipality and ward on the map, see who is running,
                and connect to official election information for all 414 Ontario municipalities.
              </p>
              <form onSubmit={submitAddress} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg">
                <label htmlFor="home-address" className="sr-only">Enter your address</label>
                <Input
                  id="home-address"
                  placeholder="Enter your address…"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  data-testid="home-address-input"
                  className="flex-1"
                />
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="home-address-submit">
                  <MapPin className="h-4 w-4 mr-1.5" /> Find My Ward
                </Button>
              </form>
              <div className="flex flex-wrap gap-3 mt-6">
                <Button asChild variant="outline" data-testid="home-election-map"><Link to="/elections/map"><MapIcon className="h-4 w-4 mr-1.5" /> Ontario Election Map</Link></Button>
                <Button asChild variant="outline"><Link to="/elections">Explore Ontario Elections</Link></Button>
              </div>
            </div>
            <div className="space-y-4">
              {elections.map((e) => {
                const muni = munis.find((m) => m.id === e.municipality_id);
                return (
                  <Card key={e.id}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <h3 className="font-heading font-semibold text-base text-foreground">{e.label}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{muni?.name}</p>
                        </div>
                        <Badge variant="outline" className={e.status === "open" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"}>
                          {e.status === "open" ? "Declarations open" : e.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        Voting: {new Date(e.voting_opens_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })} –{" "}
                        {new Date(e.voting_closes_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
              <Card className="bg-secondary">
                <CardContent className="p-5 text-xs text-muted-foreground leading-relaxed">
                  For official election information, always consult your municipal Clerk's office. Witness Your Vote links to public information and is not an official election source.
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CANDIDATE DISCOVERY & PARTICIPATION */}
      <section className="wyv-container py-14" aria-labelledby="candidates-title">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-4"><Users className="h-3 w-3 mr-1" /> Candidates</Badge>
            <h2 id="candidates-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
              Candidate discovery, participation and daily updates.
            </h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              Every eligible Ontario municipal candidate can participate on equal terms. A daily scan of
              official municipal sources also tracks newly filed candidates across the province — always
              source-linked and admin-reviewed.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button asChild data-testid="home-candidates-browse"><Link to="/candidates">Find a Candidate</Link></Button>
              <Button asChild variant="outline"><Link to="/candidate-filings">Latest Candidate Filings</Link></Button>
              <Button asChild variant="outline"><Link to="/candidate-application"><UserPlus className="h-4 w-4 mr-1.5" /> Candidate Sign-Up</Link></Button>
            </div>
          </div>
          <Card>
            <CardContent className="p-6">
              <h3 className="font-heading font-semibold text-sm text-foreground mb-3 inline-flex items-center gap-2">
                <Vote className="h-4 w-4 text-accent" /> How candidates take part
              </h3>
              <ul className="space-y-2.5">
                {[
                  "Apply once — verified against the official candidate list",
                  "Get a public profile, direct declaration link and QR tools",
                  "Never see declarant identities or private voter data",
                  "No fees, no endorsements — the platform stays neutral",
                ].map((q) => (
                  <li key={q} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> {q}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 4. LATEST NEWS */}
      <section className="bg-white border-y border-slate-200" aria-labelledby="news-title">
        <div className="wyv-container py-14">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 mb-3"><Newspaper className="h-3 w-3 mr-1" /> Newsroom</Badge>
              <h2 id="news-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Latest news</h2>
            </div>
            <Button asChild variant="outline" data-testid="home-all-news"><Link to="/news">All news</Link></Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-news-grid">
            {news.map((post) => (
              <Link key={post.id} to={`/news/${post.slug}`} className="group block">
                <Card className="h-full overflow-hidden hover:shadow-md transition-shadow duration-150">
                  {post.image && (
                    <img src={post.image} alt="" className="h-40 w-full object-cover" loading="lazy" />
                  )}
                  <CardContent className="p-5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-accent mb-2">
                      {post.published_at ? new Date(post.published_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : ""}
                      {post.featured && <span className="ml-2 text-amber-600">Featured</span>}
                    </p>
                    <h3 className="font-heading font-semibold text-base text-foreground leading-snug group-hover:text-primary">{post.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-2 line-clamp-3">{post.summary}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
            {news.length === 0 && <p className="text-sm text-muted-foreground">News is coming soon.</p>}
          </div>
        </div>
      </section>

      {/* PHASE 12 — DON'T MISS THE LAUNCH (early-access section, §11) */}
      {beta?.beta_mode && (
        <section id="early-access" className="bg-white border-b border-slate-200 scroll-mt-20"
                 aria-labelledby="dont-miss-title">
          <div className="wyv-container py-14">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-3">
                  <Sparkles className="h-3 w-3 mr-1" /> Early access
                </Badge>
                <h2 id="dont-miss-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground"
                    data-testid="dont-miss-title">
                  {beta.messaging?.dont_miss_title || "DON'T MISS THE LAUNCH"}
                </h2>
                <p className="text-muted-foreground mt-3 leading-relaxed max-w-lg" data-testid="dont-miss-body">
                  {beta.messaging?.dont_miss_body}
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  Currently in BETA — the full election-verification program is not yet officially live.{" "}
                  <Link to="/beta" className="text-primary hover:underline">What does BETA mean?</Link>
                </p>
                {/* BETA as an invitation, not a warning */}
                <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4"
                     data-testid="help-build-card">
                  <p className="font-heading font-bold text-sm text-emerald-950">HELP BUILD WITNESSYOURVOTE</p>
                  <p className="text-xs text-emerald-950/80 mt-1 leading-relaxed">
                    We're testing it with real people before the full launch. Explore it. Try it.
                    Tell us what works — and what doesn't.
                  </p>
                  <div className="mt-2.5">
                    <BetaFeedbackButton />
                  </div>
                </div>
              </div>
              <LaunchSignup variant="full" sourcePage="/ (dont-miss)" />
            </div>
          </div>
        </section>
      )}

      {/* 5. ALERTS SIGNUP */}
      <section className="bg-slate-100 border-b border-slate-200" aria-labelledby="alerts-title">
        <div className="wyv-container py-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 id="alerts-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Stay informed.</h2>
              <p className="text-muted-foreground mt-3 leading-relaxed max-w-lg">
                Election reminders, new candidates in your area, accountability research and important updates —
                delivered when they matter. Your subscription is stored completely separately from any voter
                declaration data.
              </p>
            </div>
            <AlertsSignup compact />
          </div>
        </div>
      </section>

      {/* 6. ACCOUNTABILITY (relocated from hero — supporting placement) */}
      <section className="wyv-container py-14" aria-labelledby="accountability-title">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-4"><Scale className="h-3 w-3 mr-1" /> Taxpayer watch</Badge>
            <h2 id="accountability-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
              What is your municipality paying for?
            </h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              Explore public records, technology contracts, software spending and procurement — and ask whether
              taxpayers are getting value for their money. Framed around factual questions, never unsupported claims.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button asChild data-testid="home-accountability-explore"><Link to="/accountability">Explore Municipal Spending</Link></Button>
              <Button asChild variant="outline"><Link to="/accountability/investigate">Investigate My Municipality</Link></Button>
            </div>
          </div>
          <Card>
            <CardContent className="p-6">
              <h3 className="font-heading font-semibold text-sm text-foreground mb-3">Questions we help residents ask</h3>
              <ul className="space-y-2">
                {[
                  "How much is the municipality paying?",
                  "What services were purchased — and what is actually being used?",
                  "What are the ongoing annual costs?",
                  "Were projects delivered on time and on budget?",
                  "What measurable benefit did taxpayers receive?",
                  "Were Canadian suppliers or alternatives considered?",
                ].map((q) => (
                  <li key={q} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> {q}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 7. PITA — secondary side note */}
      <section className="wyv-container pb-14" aria-labelledby="pita-note-title">
        <Card className="bg-secondary border-slate-200" data-testid="home-pita-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <img
                src="/pita-logo.png"
                alt="PITA — Public Issue Tracker App logo"
                className="h-16 w-auto rounded-md border border-slate-200 bg-white p-1 shrink-0"
                loading="lazy"
              />
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Related tool</p>
                <h2 id="pita-note-title" className="font-heading font-semibold text-lg text-foreground">
                  Interested in PITA for your municipality?
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2 max-w-2xl">
                  PITA is a related municipal issue-tracking tool currently associated with the City of Kawartha
                  Lakes demonstration on stevelesperance.com. It could potentially be made available to other
                  municipalities — contact Steve if you are interested in bringing PITA to your community.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <Button asChild size="sm" variant="outline" data-testid="home-pita-learn"><Link to="/pita">Learn About PITA</Link></Button>
                  <Button asChild size="sm" variant="outline" data-testid="home-pita-request"><Link to="/pita/request">Request PITA for My Municipality</Link></Button>
                  <a
                    href="https://stevelesperance.com/map"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-2"
                    data-testid="home-pita-demo"
                  >
                    External demonstration <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  The stevelesperance.com implementation is an external demonstration and is not part of the
                  Witness Your Vote declaration system.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="bg-white border-y border-slate-200" aria-labelledby="how-title">
        <div className="wyv-container py-14 sm:py-16">
          <div className="max-w-2xl mb-10">
            <h2 id="how-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground">How Witness Your Vote works</h2>
            <p className="text-muted-foreground mt-3">
              Five calm steps. Your official vote always comes first — witnessing is voluntary and happens after you have voted.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {STEPS.map((step, i) => (
              <motion.div key={step.title} {...fadeUp} transition={{ duration: 0.24, delay: i * 0.05 }}>
                <Card className="h-full">
                  <CardContent className="p-5">
                    <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center mb-4">
                      <step.icon className="h-5 w-5 text-primary" />
                    </div>
                    <p className="text-xs font-bold text-accent mb-1">STEP {i + 1}</p>
                    <h3 className="font-heading font-semibold text-sm text-foreground mb-2">{step.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="mt-8">
            <Button asChild variant="outline" data-testid="home-how-it-works-link"><Link to="/how-it-works">Read the full process</Link></Button>
          </div>
        </div>
      </section>

      {/* PRINCIPLE + DISCLAIMERS */}
      <section className="bg-primary text-white">
        <div className="wyv-container py-12">
          <div className="max-w-3xl">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl mb-4">Our principle</h2>
            <p className="text-blue-100 text-base sm:text-lg leading-relaxed" data-testid="home-principle-block">
              “{copy.disclaimers?.principle || COPY.principle}”
            </p>
          </div>
        </div>
      </section>

      <section className="wyv-container py-12">
        <Card className="bg-secondary border-slate-200">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-heading font-semibold text-base text-foreground mb-3">Independent. Voluntary. Not an official voting system.</h2>
            <div className="space-y-2 text-xs text-muted-foreground leading-relaxed max-w-3xl">
              <p>{copy.disclaimers?.independent || COPY.disclaimers.independent}</p>
              <p>{copy.disclaimers?.not_replacement || COPY.disclaimers.notReplacement}</p>
              <p>{copy.disclaimers?.voluntary || COPY.disclaimers.voluntary}</p>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
